import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, InputGroup, Button, Row, Col, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import useProjects from "../../Hooks/useProjects";
import useStages from "../../Hooks/useStages";
import useUsersWithLeads from "../../Hooks/useUsersWithLeads";
import { createGenericLeadApi } from "../../apiServices/genericLeadServices";

const COUNTRY_CODES = [
  { label: "+91 (IN)", value: "+91" },
  { label: "+1 (US/CA)", value: "+1" },
  { label: "+44 (UK)", value: "+44" },
  { label: "+971 (UAE)", value: "+971" },
  { label: "+966 (KSA)", value: "+966" },
  { label: "+61 (AU)", value: "+61" },
  { label: "+65 (SG)", value: "+65" },
];

const SOURCE_OPTIONS = [
  { label: "Generic Lead", value: "generic" },
  { label: "Manual Entry", value: "manual" },
  { label: "Direct Call", value: "direct_call" },
  { label: "Ai calling", value: "ai_calling" },
  { label: "Ivr calling", value: "ivr_calling" },
  { label: "RCS msg", value: "rcs_msg" },
  { label: "Whatsapp", value: "whatsapp" },
  { label: "Referral", value: "referral" },
  { label: "Walk-in", value: "walk_in" },
  { label: "Website", value: "website" },
  { label: "Facebook", value: "facebook" },
  { label: "Google", value: "google" },
  { label: "Other", value: "other" },
];

const LEAD_TYPE_OPTIONS = [
  { label: "Buyer", value: "Buyer" },
  { label: "Investor", value: "Investor" },
  { label: "Seller", value: "Seller" },
  { label: "Broker", value: "Broker" },
  { label: "Channel Partner", value: "Channel Partner" },
  { label: "Tenant", value: "Tenant" },
  { label: "Property RM", value: "Property RM" },
  { label: "Other", value: "Other" },
];

const CreateLeadModal = ({ isOpen, toggle, onSuccess }) => {
  const authUser = JSON.parse(localStorage.getItem("authUser"))?.response;
  const isSuperAdmin = authUser?.role === "super_admin";

  const { projects = [] } = useProjects({ limit: 1000 });
  const { stages = [] } = useStages({ limit: 1000 });
  const { users = [] } = useUsersWithLeads();

  const [formData, setFormData] = useState({
    name: "",
    countryCode: "+91",
    phone: "",
    email: "",
    source: "generic",
    leadType: "",
    projectId: "",
    stage: "",
    assignedTo: "",
  });

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const defaultUser = isSuperAdmin ? "" : authUser?._id || "";
      setFormData({
        name: "",
        countryCode: "+91",
        phone: "",
        email: "",
        source: "generic",
        leadType: "",
        projectId: "",
        stage: "",
        assignedTo: defaultUser,
      });
    }
  }, [isOpen, isSuperAdmin, authUser?._id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
    setFormData((prev) => ({ ...prev, phone: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      return toast.warning("Please enter Lead Full Name.");
    }

    const cleanPhone = formData.phone.replace(/\D/g, "");
    if (!cleanPhone || !/^[6-9]\d{9}$/.test(cleanPhone)) {
      return toast.warning("Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9.");
    }

    const fullPhone = `${formData.countryCode}${cleanPhone}`;

    try {
      setSubmitting(true);
      const payload = {
        name: formData.name.trim(),
        phone: fullPhone,
        email: formData.email.trim(),
        source: formData.source,
        projectId: formData.projectId || null,
        stage: formData.stage || null,
        assignedTo: formData.assignedTo || null,
        remark: formData.leadType || null,
        autoAssign: !formData.assignedTo,
      };

      const res = await createGenericLeadApi(payload);
      toast.success(res?.message || "Lead created successfully!");
      if (typeof onSuccess === "function") {
        onSuccess(res?.data);
      }
      toggle();
    } catch (err) {
      toast.error(err?.message || err?.response?.data?.message || "Failed to create lead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle} className="bg-light">
        <i className="mdi mdi-account-plus me-2 text-primary" />
        Create New Lead
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <Row>
            {/* Full Name */}
            <Col md={6}>
              <FormGroup>
                <Label for="lead-name" className="fw-semibold">
                  Full Name <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  id="lead-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter full name"
                  required
                />
              </FormGroup>
            </Col>

            {/* Phone Number with Country Code */}
            <Col md={6}>
              <FormGroup>
                <Label for="lead-phone" className="fw-semibold">
                  Mobile Number <span className="text-danger">*</span>
                </Label>
                <InputGroup>
                  <Input
                    type="select"
                    name="countryCode"
                    value={formData.countryCode}
                    onChange={handleChange}
                    style={{ maxWidth: "110px" }}
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </Input>
                  <Input
                    type="tel"
                    id="lead-phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    placeholder="10-digit mobile number"
                    maxLength="10"
                    required
                  />
                </InputGroup>
                {formData.phone && !/^[6-9]\d{9}$/.test(formData.phone) ? (
                  <small className="text-danger mt-1 d-block" style={{ fontSize: "11px" }}>
                    Must start with 6, 7, 8, or 9 and be 10 digits.
                  </small>
                ) : (
                  <small className="text-muted mt-1 d-block" style={{ fontSize: "11px" }}>
                    Starting with 6-9 (10 digits).
                  </small>
                )}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            {/* Email Address */}
            <Col md={6}>
              <FormGroup>
                <Label for="lead-email" className="fw-semibold">
                  Email Address
                </Label>
                <Input
                  type="email"
                  id="lead-email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                />
              </FormGroup>
            </Col>

            {/* Source */}
            <Col md={6}>
              <FormGroup>
                <Label for="lead-source" className="fw-semibold">
                  Lead Source
                </Label>
                <Input
                  type="select"
                  id="lead-source"
                  name="source"
                  value={formData.source}
                  onChange={handleChange}
                >
                  {SOURCE_OPTIONS.map((src) => (
                    <option key={src.value} value={src.value}>
                      {src.label}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            {/* Lead Type */}
            <Col md={6}>
              <FormGroup>
                <Label for="lead-type" className="fw-semibold">
                  Lead Type
                </Label>
                <Input
                  type="select"
                  id="lead-type"
                  name="leadType"
                  value={formData.leadType}
                  onChange={handleChange}
                >
                  {LEAD_TYPE_OPTIONS.map((lt) => (
                    <option key={lt.value} value={lt.value}>
                      {lt.label}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>

            {/* Project */}
            <Col md={6}>
              <FormGroup>
                <Label for="lead-project" className="fw-semibold">
                  Project
                </Label>
                <Input
                  type="select"
                  id="lead-project"
                  name="projectId"
                  value={formData.projectId}
                  onChange={handleChange}
                >
                  <option value="">-- Select Project --</option>
                  {projects.map((proj) => (
                    <option key={proj._id} value={proj._id}>
                      {proj.projectName}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            {/* Lead Stage */}
            <Col md={6}>
              <FormGroup>
                <Label for="lead-stage" className="fw-semibold">
                  Initial Stage
                </Label>
                <Input
                  type="select"
                  id="lead-stage"
                  name="stage"
                  value={formData.stage}
                  onChange={handleChange}
                >
                  <option value="">-- Default (New Lead) --</option>
                  {stages.map((stg) => (
                    <option key={stg._id} value={stg._id}>
                      {stg.name}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>

            {/* Assigned To */}
            <Col md={6}>
              <FormGroup>
                <Label for="lead-assigned" className="fw-semibold">
                  Assigned To
                </Label>
                <Input
                  type="select"
                  id="lead-assigned"
                  name="assignedTo"
                  value={formData.assignedTo}
                  onChange={handleChange}
                >
                  {isSuperAdmin ? (
                    <option value="">-- Auto Assign / Unassigned --</option>
                  ) : (
                    <option value={authUser?._id || ""}>Self ({authUser?.firstName} {authUser?.lastName})</option>
                  )}
                  {users.map((usr) => (
                    <option key={usr._id} value={usr._id}>
                      {usr.firstName} {usr.lastName} ({usr.role || "Employee"})
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>


        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle} disabled={submitting}>
            Cancel
          </Button>
          <Button color="primary" type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner size="sm" className="me-2" /> Creating...
              </>
            ) : (
              <>
                <i className="mdi mdi-check me-1" /> Create Lead
              </>
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CreateLeadModal;
